import { apiFetch } from "@/services/apiClient";
import type { UserRoleValue } from "@/lib/adminConstants";
import { API_URL } from "@/lib/utils";

export interface AdminUser {
  id: string;
  name: string;
  email: string;
  role: UserRoleValue;
  emp_dept_permission: string[];
  emp_office_location_permission: string[];
  emp_grade_permission: string[];
  emp_dept: string[];
  emp_grade: string | null;
  emp_office: string[];
  is_active: boolean;
  is_employee: boolean;
}

export interface CreateUserPayload {
  username: string;
  email: string;
  password: string;
  role: UserRoleValue;
  emp_dept: string[];
  emp_office: string[];
  emp_grade: string | null;
  is_employee: boolean;
  emp_dept_permission: string[];
  emp_office_location_permission: string[];
  emp_grade_permission: string[];
}

async function readError(res: Response, fallback: string): Promise<string> {
  try {
    const body = await res.json();
    if (typeof body?.detail === "string") return body.detail;
    if (Array.isArray(body?.detail) && body.detail[0]?.msg) return body.detail[0].msg;
  } catch {
    /* ignore */
  }
  return fallback;
}

interface RawAdminUser {
  _id?: string;
  id?: string;
  name: string;
  email: string;
  role: UserRoleValue;
  emp_dept_permission: string[] | null;
  emp_office_location_permission: string[] | null;
  emp_grade_permission: string[] | null;
  emp_dept: string[] | null;
  emp_grade: string | null;
  emp_office: string[] | null;
  is_active: boolean | null;
  is_employee: boolean | null;
}

function normalizeUser(raw: RawAdminUser): AdminUser {
  return {
    id: raw._id ?? raw.id ?? "",
    name: raw.name,
    email: raw.email,
    role: raw.role,
    emp_dept_permission: raw.emp_dept_permission ?? [],
    emp_office_location_permission: raw.emp_office_location_permission ?? [],
    emp_grade_permission: raw.emp_grade_permission ?? [],
    emp_dept: raw.emp_dept ?? [],
    emp_grade: raw.emp_grade ?? null,
    emp_office: raw.emp_office ?? [],
    is_active: raw.is_active ?? true,
    is_employee: raw.is_employee ?? false,
  };
}

export async function listUsers(): Promise<AdminUser[]> {
  const res = await apiFetch(`${API_URL}/user/users`, { method: "GET" });
  if (!res.ok) throw new Error(await readError(res, "Failed to load users"));
  const data = (await res.json()) as RawAdminUser[];
  return data.map(normalizeUser);
}

/** Distinct location values (legacy_location) for the location-permission picker. */
export async function fetchLocations(): Promise<string[]> {
  const res = await apiFetch(`${API_URL}/user/locations`, { method: "GET" });
  if (!res.ok) throw new Error(await readError(res, "Failed to load locations"));
  return res.json();
}

/** Distinct department values (service_line) for the department-permission picker. */
export async function fetchDepartments(): Promise<string[]> {
  const res = await apiFetch(`${API_URL}/user/departments`, { method: "GET" });
  if (!res.ok) throw new Error(await readError(res, "Failed to load departments"));
  return res.json();
}

/** Distinct grade values (Emp Category) for the grade-permission picker. */
export async function fetchGrades(): Promise<string[]> {
  const res = await apiFetch(`${API_URL}/user/grades`, { method: "GET" });
  if (!res.ok) throw new Error(await readError(res, "Failed to load grades"));
  return res.json();
}

export async function createUser(payload: CreateUserPayload): Promise<void> {
  const res = await apiFetch(`${API_URL}/user/register`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error(await readError(res, "Failed to create user"));
}

export async function deleteUser(userId: string): Promise<void> {
  const res = await apiFetch(`${API_URL}/user/user_delete/${userId}`, { method: "DELETE" });
  if (!res.ok) throw new Error(await readError(res, "Failed to delete user"));
}

export async function updateUserRole(userId: string, role: UserRoleValue): Promise<void> {
  const res = await apiFetch(`${API_URL}/user/${userId}/role`, {
    method: "PATCH",
    body: JSON.stringify({ role }),
  });
  if (!res.ok) throw new Error(await readError(res, "Failed to update role"));
}

export async function resetUserPassword(userId: string): Promise<{ temp_password: string }> {
  const res = await apiFetch(`${API_URL}/user/${userId}/password`, { method: "PATCH" });
  if (!res.ok) throw new Error(await readError(res, "Failed to reset password"));
  return res.json();
}

export interface PermissionsPayload {
  emp_dept_permission: string[];
  emp_office_location_permission: string[];
  emp_grade_permission: string[];
}

export async function updateUserPermissions(
  userId: string,
  payload: PermissionsPayload,
): Promise<void> {
  const res = await apiFetch(`${API_URL}/user/${userId}/permissions`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error(await readError(res, "Failed to update permissions"));
}

export interface AttributesPayload {
  emp_dept: string[];
  emp_grade: string | null;
  emp_office: string[];
  is_employee: boolean;
}

export async function updateUserAttributes(
  userId: string,
  payload: AttributesPayload,
): Promise<void> {
  const res = await apiFetch(`${API_URL}/user/${userId}/attributes`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error(await readError(res, "Failed to update attributes"));
}
